import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useGetBuyerInventoryPriorityGroups,
  useGetUserPreferences,
  useUpdateUserPreferences,
} from '~/api';
import type {
  BuyerInventoryPriorityGroupsQueryParams,
  PriorityGroup,
} from '~/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import {
  applyColumnOrder,
  Table,
  useColumnOrderPreference,
  type TableRow,
} from '~/components/ui/table';
import type {
  BuyerInventoryFiltersInput,
  BuyerInventoryPriorityGroupsQuery,
} from '~/graphql/graphql';
import { usePaginatedQuery, type RouteSort } from '~/hooks/usePaginatedQuery';
import type { PaginatedResponse } from '~/types';
import { AddToBuyListModal } from './AddToBuyListModal';
import { EditInventoryModal } from './EditInventoryModal';
import {
  buildGroupDetailRows,
  buildGroupSummaryRow,
  buildQueueColumns,
} from './priorityGroupRows';
import {
  groupLocationKey,
  usePriorityGroupSubRows,
} from './usePriorityGroupSubRows';

// UI column key -> backend sort-field whitelist entry (BuyerInventoryGroupSortJpql).
const SORT_FIELDS: Partial<Record<string, string>> = {
  location: 'location',
  product: 'product',
  stock: 'available',
  onOrder: 'onOrder',
  requested: 'requested',
  inbound: 'inbound',
  usageRatePerWeek: 'usageRatePerWeek',
  weeksCovered: 'weeksCovered',
};

type PriorityGroupTableProps = {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly filters?: BuyerInventoryFiltersInput;
  readonly includeInventoryLengths: boolean;
};

export function PriorityGroupTable({
  t,
  filters,
  includeInventoryLengths,
}: Readonly<PriorityGroupTableProps>) {
  const navigate = useNavigate();

  // In-memory only, default collapsed; resets on reload.
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const toggleGroup = useCallback(
    (groupId: string) =>
      setExpandedGroupIds((prev) => {
        const next = new Set(prev);
        if (!next.delete(groupId)) {
          next.add(groupId);
        }
        return next;
      }),
    []
  );

  const [sort, setSort] = useState<RouteSort | undefined>();
  const [addToBuyListGroup, setAddToBuyListGroup] =
    useState<PriorityGroup | null>(null);

  const queryParams = useMemo<BuyerInventoryPriorityGroupsQueryParams>(
    () => ({ filters, includeInventoryLengths }),
    [filters, includeInventoryLengths]
  );

  const {
    data: groups,
    sentinelRef,
    isLoadingMore,
    isLoading,
    hasMoreData,
    error,
    pagination,
  } = usePaginatedQuery<
    BuyerInventoryPriorityGroupsQuery,
    PriorityGroup,
    BuyerInventoryPriorityGroupsQueryParams
  >({
    fetchHook: useGetBuyerInventoryPriorityGroups,
    select: (data) =>
      data?.buyerInventoryPriorityGroups as PaginatedResponse<PriorityGroup>,
    // Each page pays the full GROUP BY scan+sort regardless of LIMIT, so fewer,
    // larger pages cut DB work for near-zero bandwidth cost.
    // catchUpOnSortChange: re-sorting must not throw away the groups the user has already
    // scrolled to — the list reloads to the same depth in the new order.
    options: {
      filters: queryParams,
      pageSize: 25,
      sort,
      catchUpOnSortChange: true,
    },
  });

  const {
    locationsByGroupKey,
    tableControls,
    openEdit,
    editingInventory,
    showEditInventory,
    setShowEditInventory,
    closeEdit,
    handleEditModalSaved,
  } = usePriorityGroupSubRows(groups, expandedGroupIds, (key, direction) => {
    const field = SORT_FIELDS[key];
    setSort(
      field && direction
        ? [{ field, direction: direction.toUpperCase() as 'ASC' | 'DESC' }]
        : undefined
    );
  });

  const { t: tGeneral } = useTranslation('general');
  const { data: preferencesData } = useGetUserPreferences();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const { order, handleColumnReorderEnd } = useColumnOrderPreference(
    preferencesData?.userPreferences?.buyerInventoryGroupedColumnOrder,
    (newOrder, { onError, onSuccess }) =>
      updatePreferences(
        { preferences: { buyerInventoryGroupedColumnOrder: newOrder } },
        {
          onSuccess,
          onError: () => {
            onError();
            toast.error(tGeneral('table.reorder_save_error'));
          },
        }
      )
  );
  const columns = useMemo(
    () => applyColumnOrder(buildQueueColumns(t), order),
    [t, order]
  );

  const rows = useMemo<TableRow[]>(() => {
    const context = { t, navigate };
    return groups.flatMap((group, groupIndex) => {
      const expanded = expandedGroupIds.has(group.id);
      const summary = buildGroupSummaryRow(group, context, {
        groupIndex,
        isExpanded: expanded,
        onToggle: toggleGroup,
        onAddToBuyList: setAddToBuyListGroup,
      });
      if (!expanded) {
        return [summary];
      }
      const result = locationsByGroupKey.get(
        groupLocationKey(group.facility.id, group.platformProductsLength.id)
      );
      return [
        summary,
        ...buildGroupDetailRows(group, result, openEdit, context),
      ];
    });
  }, [
    groups,
    expandedGroupIds,
    locationsByGroupKey,
    t,
    navigate,
    toggleGroup,
    openEdit,
  ]);

  let tableName = t('inventory.groups');
  if (isLoading) tableName = t('inventory.loading');
  else if (error) tableName = t('inventory.load_failed');

  return (
    <div className="overflow-x-auto">
      <Table
        minWidth={60}
        rows={rows}
        columns={columns}
        tableControls={tableControls}
        totalRowsCount={pagination?.totalElements ?? 0}
        name={tableName}
        enableColumnReorder
        onColumnReorderEnd={handleColumnReorderEnd}
        striped
      />
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">{t('inventory.loading')}</div>
        </div>
      )}
      {hasMoreData === false && !isLoading && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">
            {t('inventory.no_more_items')}
          </div>
        </div>
      )}

      <div
        ref={sentinelRef}
        id="scroll-sentinel"
        style={{ height: '1px', background: 'transparent' }}
        data-testid="scroll-sentinel"
      />

      <Modal isOpen={showEditInventory} onClose={closeEdit}>
        {editingInventory && (
          <EditInventoryModal
            setShowEditInventory={setShowEditInventory}
            inventory={editingInventory}
            onSaved={handleEditModalSaved}
          />
        )}
      </Modal>

      {addToBuyListGroup && (
        <AddToBuyListModal
          onClose={() => setAddToBuyListGroup(null)}
          location={addToBuyListGroup.targetInventory.location}
          platformProductsLength={addToBuyListGroup.platformProductsLength}
        />
      )}
    </div>
  );
}
